import React, { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { formsService } from "@/services/formsService";
import { isCheckboxField, normalizeFormFields } from "@/lib/normalizeForm";

/**
 * Renders a database-configured form and stores submissions.
 */
export default function DynamicForm({ form }) {
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  if (!form) return null;

  const fields = normalizeFormFields(form.fields);

  const set = (name, value) => setValues((v) => ({ ...v, [name]: value }));

  function toggleCheckbox(name, option, checked) {
    setValues((v) => {
      const current = Array.isArray(v[name]) ? v[name] : [];
      const next = checked ? [...current, option] : current.filter((item) => item !== option);
      return { ...v, [name]: next };
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();

    for (const field of fields) {
      const isCheckboxGroup = isCheckboxField(field);
      if (!isCheckboxGroup || !field.required) continue;
      const selected = values[field.name];
      if (!Array.isArray(selected) || selected.length === 0) {
        toast.error(`Please select at least one option for “${field.label}”.`);
        return;
      }
    }

    setSubmitting(true);
    try {
      await formsService.submit(form.slug, values);
      setDone(true);
      toast.success("Thank you — your submission has been received.");
    } catch (err) {
      toast.error(err.message || "Could not submit, please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div className="text-center py-10" data-testid={`form-success-${form.slug}`}>
        <CheckCircle2 size={44} className="mx-auto text-green-700" />
        <p className="mt-4 font-serif text-2xl font-semibold text-ink">Submission received</p>
        <p className="mt-2 text-sm text-ink-soft">
          Thank you. The BCS office will get back to you shortly.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid={`dynamic-form-${form.slug}`}>
      {fields.map((field) => {
        const isCheckboxGroup = isCheckboxField(field);
        const selected = Array.isArray(values[field.name]) ? values[field.name] : [];
        const inputType = ["text", "email", "tel", "date", "number", "url"].includes(field.type)
          ? field.type
          : "text";

        return (
        <div key={field.name}>
          {isCheckboxGroup ? (
            <p className="label-bcs">
              {field.label}
              {field.required && <span className="text-maroon ml-0.5">*</span>}
            </p>
          ) : (
            <label className="label-bcs" htmlFor={`${form.slug}-${field.name}`}>
              {field.label}
              {field.required && <span className="text-maroon ml-0.5">*</span>}
            </label>
          )}
          {field.type === "textarea" ? (
            <textarea
              id={`${form.slug}-${field.name}`}
              className="input-bcs min-h-[110px]"
              required={field.required}
              value={values[field.name] || ""}
              onChange={(e) => set(field.name, e.target.value)}
              data-testid={`form-field-${field.name}`}
            />
          ) : field.type === "select" ? (
            <select
              id={`${form.slug}-${field.name}`}
              className="input-bcs"
              required={field.required}
              value={values[field.name] || ""}
              onChange={(e) => set(field.name, e.target.value)}
              data-testid={`form-field-${field.name}`}
            >
              <option value="">Select…</option>
              {(field.options || []).map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          ) : isCheckboxGroup ? (
            <div
              className="mt-2 space-y-2 rounded-lg border border-gray-200 bg-cream/40 p-4"
              role="group"
              aria-labelledby={`${form.slug}-${field.name}-legend`}
              data-testid={`form-field-${field.name}`}
            >
              <span id={`${form.slug}-${field.name}-legend`} className="sr-only">
                {field.label}
              </span>
              {(field.options || []).map((opt) => {
                const id = `${form.slug}-${field.name}-${opt.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`;
                const checked = selected.includes(opt);
                return (
                  <label
                    key={opt}
                    htmlFor={id}
                    className="flex items-start gap-2.5 text-sm text-ink cursor-pointer"
                  >
                    <input
                      id={id}
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 accent-maroon shrink-0"
                      checked={checked}
                      onChange={(e) => toggleCheckbox(field.name, opt, e.target.checked)}
                      data-testid={`form-field-${field.name}-${opt.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                    />
                    <span>{opt}</span>
                  </label>
                );
              })}
            </div>
          ) : (
            <input
              id={`${form.slug}-${field.name}`}
              type={inputType}
              className="input-bcs"
              required={field.required}
              value={values[field.name] || ""}
              onChange={(e) => set(field.name, e.target.value)}
              data-testid={`form-field-${field.name}`}
            />
          )}
        </div>
        );
      })}
      <button type="submit" className="btn-primary w-full sm:w-auto" disabled={submitting} data-testid={`form-submit-${form.slug}`}>
        {submitting ? "Submitting…" : "Submit"}
      </button>
    </form>
  );
}
